import { useEffect, useState } from 'react';
import { companyService } from '@root/app/shared/new-services/company.service';

/**
 * Module-level cache so every caller of `useRewardsEnabled` reuses the same
 * in-flight fetch / resolved value (mirrors `useActionsEnabled`). Gates the
 * Rewards sidebar entry and the top-level Rewards section on the company's
 * `rewardsEnabled` feature flag.
 */
let cachedValue: boolean | null = null;
let inflight: Promise<boolean> | null = null;
const subscribers = new Set<(value: boolean) => void>();

const fetchRewardsEnabled = (): Promise<boolean> => {
    if (cachedValue !== null) {
        return Promise.resolve(cachedValue);
    }
    if (inflight) {
        return inflight;
    }
    inflight = new Promise<boolean>((resolve) => {
        companyService.getConfigKey('rewardsEnabled', (err, value) => {
            const resolved = !err && Boolean(value);
            cachedValue = resolved;
            inflight = null;
            subscribers.forEach((notify) => notify(resolved));
            resolve(resolved);
        });
    });
    return inflight;
};

const useRewardsEnabled = (): boolean => {
    const [enabled, setEnabled] = useState<boolean>(cachedValue ?? false);

    useEffect(() => {
        let active = true;
        if (cachedValue !== null) {
            setEnabled(cachedValue);
            return undefined;
        }

        const notify = (value: boolean) => {
            if (active) { setEnabled(value); }
        };
        subscribers.add(notify);
        fetchRewardsEnabled().then((value) => {
            if (active) { setEnabled(value); }
        });
        return () => {
            active = false;
            subscribers.delete(notify);
        };
    }, []);

    return enabled;
};

export default useRewardsEnabled;
